import { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { cn } from '@finn-auto/design-system/helpers/cn';
import { COLORS } from './format';

/**
 * Snooze a candidate off the SLA "act now" list until a chosen time, WITH a
 * mandatory reason — so the flag is muted deliberately, not lost (reported
 * Aug 2026: a candidate held because they were progressing in another process
 * had no way to be parked). Presets are relative to now (timezone-proof); the
 * custom date wakes at 09:00 the recruiter's local time, which for FINN is
 * Europe/Berlin — the same zone the rest of the board renders in.
 */

/** Mirrors SUGGESTED_SNOOZE_REASONS in proxy lib/taSlaSnooze.js. */
const QUICK_REASONS = [
  'In another process',
  'Awaiting candidate availability',
  'Waiting on hiring manager',
  'Holiday / out of office',
];

type DurationKey = '3d' | '1w' | '2w' | 'custom';

const PRESETS: { key: DurationKey; label: string; days: number }[] = [
  { key: '3d', label: '3 days', days: 3 },
  { key: '1w', label: '1 week', days: 7 },
  { key: '2w', label: '2 weeks', days: 14 },
];

/**
 * The wake-up instant as ISO, or null if invalid. Presets are relative to `nowMs`
 * (timezone-proof); custom wakes at 09:00 local on the chosen day. Pure in nowMs so
 * it is only ever called from an event handler, never during render (React purity).
 */
function computeWakeIso(duration: DurationKey, customDate: string, nowMs: number): string | null {
  if (duration === 'custom') {
    if (!customDate) return null;
    const ms = new Date(`${customDate}T09:00:00`).getTime();
    return Number.isFinite(ms) && ms > nowMs ? new Date(ms).toISOString() : null;
  }
  const days = PRESETS.find((p) => p.key === duration)?.days ?? 7;
  return new Date(nowMs + days * 86_400_000).toISOString();
}

export interface SnoozeSubmit {
  snoozedUntil: string;
  reason: string;
  tags: string[];
}

export function SlaSnoozeModal({
  candidateName,
  leverUrl,
  onClose,
  onConfirm,
}: {
  candidateName: string;
  leverUrl: string;
  onClose: () => void;
  onConfirm: (input: SnoozeSubmit) => Promise<void>;
}) {
  const [duration, setDuration] = useState<DurationKey>('1w');
  const [customDate, setCustomDate] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A reason is mandatory; a custom duration also needs a date. The future-date
  // check itself happens at submit (it needs `now`, impure in render).
  const hasReason = tags.length > 0 || freeText.trim().length > 0;
  const canSubmit = hasReason && (duration !== 'custom' || Boolean(customDate)) && !submitting;

  const toggleTag = (t: string) =>
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const submit = async () => {
    if (!canSubmit) return;
    const snoozedUntil = computeWakeIso(duration, customDate, Date.now());
    if (!snoozedUntil) {
      setError('Choose a date in the future.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm({ snoozedUntil, reason: freeText.trim(), tags });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not snooze this candidate.');
      setSubmitting(false);
    }
  };

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,32rem)] gap-5 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle>Snooze {candidateName}</ModalTitle>
        </ModalHeader>

        <p className="text-sm text-gray-500">
          Parks this candidate off the SLA list until the chosen time. A reason is required so the
          team can see why it was held.
        </p>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-600">Snooze for</div>
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <Chip key={p.key} active={duration === p.key} onClick={() => setDuration(p.key)}>
                {p.label}
              </Chip>
            ))}
            <Chip active={duration === 'custom'} onClick={() => setDuration('custom')}>
              Custom date
            </Chip>
          </div>
          {duration === 'custom' && (
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="mt-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          )}
        </div>

        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-600">
            Reason <span className="text-red-500">*</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {QUICK_REASONS.map((r) => (
              <Chip key={r} active={tags.includes(r)} onClick={() => toggleTag(r)}>
                {r}
              </Chip>
            ))}
          </div>
          <Textarea
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Add a note (optional if you picked a reason above)…"
            rows={2}
            maxLength={500}
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* Lever's API can't be snoozed from here, so this is board-only. The link lets a
            recruiter mirror it in Lever by hand if they want — deliberately NOT synced: whatever
            is set here is what the board shows and un-snoozes, and it overrides a Lever snooze. */}
        <p className="text-xs text-gray-400">
          This snooze applies to the board only — it doesn't change Lever.{' '}
          <a
            href={leverUrl}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-blue-600 hover:underline"
          >
            Also snooze in Lever ↗
          </a>{' '}
          if you like (not synced; the board snooze takes precedence here).
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Snoozing…' : 'Snooze'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

/** A pill toggle — reused for both the duration and reason choices. */
function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-colors',
        active ? 'ring-transparent' : 'bg-white text-gray-600 ring-gray-300 hover:bg-gray-50'
      )}
      style={active ? { backgroundColor: COLORS.blueBg, color: COLORS.blueText } : undefined}
    >
      {children}
    </button>
  );
}
